/**
 * Stable view models used by screens and design-system components.
 *
 * Adapters decouple the UI from wire formats (generated OpenAPI types,
 * mock-server shapes, upstream RMF responses) so contract changes can be
 * absorbed here rather than spread across screens.
 *
 * NOTE: The concrete upstream types are still being reconciled in the parity
 * gate. Input shapes are typed as `unknown` and cast to the expected scaffold
 * shape; the casts will be removed/replaced with real generated types once the
 * ADR locks the Phase-1 dispatcher/map/transport scope.
 */
import type { LiftMode, LiftSessionView, LiftStandingRequest } from './v1';

export interface RobotViewModel {
  id: string;
  name: string;
  fleet: string;
  vendor: string;
  status: string;
  battery?: number;
  level?: string;
  coordinates?: { x: number; y: number };
  lastSeen?: string;
}

export function adaptRobot(r: unknown): RobotViewModel {
  const raw = r as Record<string, unknown>;
  const location = raw.location as Record<string, unknown> | undefined;
  // Upstream rmf_api_msgs Location2D renamed `level` → `map`
  // (required: ["map","x","y","yaw"]). Tolerate the legacy alias
  // so a stale upstream doesn't render the canvas empty.
  const mapName =
    typeof location?.map === 'string'
      ? location.map
      : typeof location?.level === 'string'
        ? location.level
        : undefined;
  // Upstream emits battery as 0.0–1.0 (state of charge, not
  // percentage). The view-model and UI expect 0–100 so operators
  // see meaningful numbers; a 0.77 charge is "77%", not "0.77%".
  const batteryRaw = typeof raw.battery === 'number' ? raw.battery : undefined;
  return {
    id: `${raw.fleet}/${raw.name}`,
    name: String(raw.name ?? ''),
    fleet: String(raw.fleet ?? ''),
    vendor: String(raw.vendor ?? 'unknown'),
    status: String(raw.status ?? 'unknown'),
    battery: batteryRaw != null ? Math.round(batteryRaw * 100) : undefined,
    level: mapName,
    coordinates: location ? { x: Number(location.x ?? 0), y: Number(location.y ?? 0) } : undefined,
    lastSeen: raw.lastSeen as string | undefined,
  };
}

export interface TaskViewModel {
  id: string;
  type: string;
  status: string;
  assignedRobot?: string;
  triggeredBy?: string;
  startedAt?: string;
  durationSec?: number;
}

export function adaptTask(t: unknown): TaskViewModel {
  const raw = t as Record<string, unknown>;
  return {
    id: String(raw.id ?? ''),
    type: String(raw.type ?? ''),
    status: String(raw.status ?? 'unknown'),
    assignedRobot: raw.assignedRobot as string | undefined,
    triggeredBy: raw.triggeredBy as string | undefined,
    startedAt: raw.startedAt as string | undefined,
    durationSec: raw.durationSec as number | undefined,
  };
}

export interface AlertViewModel {
  id: string;
  severity: string;
  source: string;
  message: string;
  /** Free-form subtitle (often the short, single-line summary the
   *  publisher uses to surface the robot name + battery %). The
   *  bell + toast display this verbatim under the message. */
  subtitle?: string;
  /** Category like `robot` / `system` / `task`. Used by the bell to
   *  filter out noise (e.g. the system heartbeat) from the dropdown. */
  category?: string;
  timestamp: string;
  status: string;
  isOpen: boolean;
  /** Routing metadata emitted by the publisher for click-through. */
  navigateTo?: string;
  /** `robot:<name>` / `task:<id>` / `door:<name>` etc. */
  deepLink?: string;
  /** Structured fields for the specific entity (e.g. battery %). */
  robotName?: string;
  robotBattery?: number;
  taskId?: string;
  /** Ack/resolve metadata (Workstream C2's real backend alerts —
   *  absent for anything still on the legacy/mock alert shape). */
  ackedBySub?: string;
  ackedAtMs?: number;
  resolvedBySub?: string;
  resolvedAtMs?: number;
  /** The label snapshotted from the actor's token at the moment they
   *  acted. Absent on a row whose token carried no email claim, and on a
   *  pre-0.6.32 row whose actor has no `known_users` entry -- migration
   *  0033 backfills the rest, which is the whole reason it exists.
   *  Either way the Acked column falls back to the subject, which is
   *  honest rather than blank. */
  ackedByEmail?: string;
  resolvedByEmail?: string;
  /** Which rule raised the row and what it is about, as `AlertOut`
   *  carries them (`api/alerts.py`). Absent on the legacy/mock row
   *  shape, which carries none of them. The attention list and the
   *  hotel-word toasts read these (twin slice 1, contract §6); there
   *  is no parallel record type. */
  rule?: string;
  sourceKind?: string;
  sourceId?: string;
  /** When the row opened, epoch ms, as a NUMBER. `timestamp` is the
   *  same value stringified for the bell's sort; this one is for
   *  anything that does arithmetic or writes a clock time. */
  openedAtMs?: number;
  /** The row's own `fingerprint` column: `<rule>:<sourceId>`, plus the
   *  category or refusal code for `robot_issue`/`lift_refusal`.
   *  api-server 0.6.37 and later. */
  fingerprint?: string;
  /** The acker's known roles at the moment of the ack, BARE and sorted
   *  (migration 0035). `null` is the server saying "not known" -- no
   *  ack, an ack from before 0035, or roles that belong to an earlier
   *  ack -- and is never read as "none"; `[]` is "known, and none".
   *  Absent from a server before 0.6.37, which means the same as null. */
  ackedByRoles?: readonly string[] | null;
  /** The highest of `ackedByRoles` by the server's precedence. Null or
   *  absent exactly when no role is known. */
  ackedByRole?: string | null;
}

/** `ackedByRoles` off the wire: the role names when the server sent a
 *  list, `null` when it sent null, and absent for anything else. A null
 *  is never turned into `[]`, which would claim "known, and none". */
function ackedRolesOf(v: unknown): readonly string[] | null | undefined {
  if (v === null) return null;
  if (!Array.isArray(v)) return undefined;
  return v.filter((r): r is string => typeof r === 'string');
}

export function adaptAlert(a: unknown): AlertViewModel {
  const raw = a as Record<string, unknown>;
  const dl = typeof raw.deep_link === 'string' ? raw.deep_link : undefined;
  // The real backend (`api/alerts.py::AlertOut`, Workstream C1/C2)
  // uses `sourceKind`/`sourceId`/`openedAtMs`/`lastSeenAtMs` — synthesize
  // the fields the legacy shape's callers expect (`deepLink`, `category`,
  // `timestamp`) from those rather than requiring the backend to know
  // about this frontend-only view-model's conventions.
  const sourceKind = typeof raw.sourceKind === 'string' ? raw.sourceKind : undefined;
  const sourceId = typeof raw.sourceId === 'string' ? raw.sourceId : undefined;
  const synthesizedDeepLink = sourceKind && sourceId ? `${sourceKind}:${sourceId}` : undefined;
  return {
    id: String(raw.id ?? ''),
    severity: String(raw.tier ?? raw.severity ?? 'info'),
    source: String(raw.source ?? sourceId ?? raw.title ?? ''),
    message: String(raw.message ?? raw.subtitle ?? ''),
    subtitle: typeof raw.subtitle === 'string' ? raw.subtitle : undefined,
    category: typeof raw.category === 'string' ? raw.category : sourceKind,
    // Precedence is load-bearing, not cosmetic. `openedAtMs` is the
    // bell's sort key by contract (`alerts_engine.py::_snapshot_row`
    // publishes it on the live path for exactly that reason, and
    // `test_notification_prefs.py` pins it), so nothing may be
    // resolved ahead of it that a real `AlertOut` row also carries —
    // `lastSeenAtMs` in particular would re-sort the bell on every
    // re-observation of an alert that opened hours ago.
    //
    // `timestamp` comes LAST and only for the mock publisher's legacy
    // row shape (`tools/mock-rmf-server/src/zenoh-publisher.ts`),
    // which carries no epoch field at all. Without it those rows
    // adapt to `''` and the Time column reads `—` against the dev
    // stack. It was the previous `lastSeenMs` entry's slot; that name
    // belongs to `IssueOut` (`api/issues.py`), never reaches an alert
    // row, and so only ever matched nothing.
    timestamp: String(
      raw.unix_millis_time ??
        raw.unix_millis_alert_time ??
        raw.openedAtMs ??
        raw.timestamp ??
        '',
    ),
    status: String(raw.status ?? raw.state ?? 'open'),
    isOpen: (raw.status ?? raw.state) === 'open',
    navigateTo: typeof raw.navigate_to === 'string' ? raw.navigate_to : undefined,
    deepLink: dl ?? synthesizedDeepLink,
    robotName: typeof raw.robot_name === 'string' ? raw.robot_name : (sourceKind === 'robot' ? sourceId : undefined),
    robotBattery: typeof raw.robot_battery === 'number' ? raw.robot_battery : undefined,
    taskId: typeof raw.task_id === 'string' ? raw.task_id : undefined,
    ackedBySub: typeof raw.ackedBySub === 'string' ? raw.ackedBySub : undefined,
    ackedAtMs: typeof raw.ackedAtMs === 'number' ? raw.ackedAtMs : undefined,
    ackedByEmail: typeof raw.ackedByEmail === 'string' ? raw.ackedByEmail : undefined,
    resolvedBySub: typeof raw.resolvedBySub === 'string' ? raw.resolvedBySub : undefined,
    resolvedAtMs: typeof raw.resolvedAtMs === 'number' ? raw.resolvedAtMs : undefined,
    resolvedByEmail: typeof raw.resolvedByEmail === 'string' ? raw.resolvedByEmail : undefined,
    rule: typeof raw.rule === 'string' ? raw.rule : undefined,
    sourceKind,
    sourceId,
    openedAtMs:
      typeof raw.openedAtMs === 'number' && Number.isFinite(raw.openedAtMs)
        ? raw.openedAtMs
        : undefined,
    fingerprint: typeof raw.fingerprint === 'string' ? raw.fingerprint : undefined,
    ackedByRoles: ackedRolesOf(raw.ackedByRoles),
    ackedByRole:
      typeof raw.ackedByRole === 'string'
        ? raw.ackedByRole
        : raw.ackedByRole === null
          ? null
          : undefined,
  };
}

export type DoorStateValue = 'open' | 'closed' | 'moving' | 'fault' | 'unknown';

export interface DoorLastCommandView {
  source: 'console' | 'robot' | 'other';
  actor: string;
  requested: 'open' | 'close';
  atMs: number;
}

export interface DoorViewModel {
  id: string;
  name: string;
  level: string;
  state: DoorStateValue;
  doorType?: number;
  v1?: { x: number; y: number };
  v2?: { x: number; y: number };
  lastSeen?: string;
  siteId?: string;
  /**
   * Whether anything is publishing state for this door, as opposed to
   * the door being known only from the building map's inventory
   * (`doors_registry`).
   *
   * Its own field, and never inferred from `state === 'unknown'`: a
   * live door that reports MODE_UNKNOWN and a door whose adapter is
   * dead are two different situations with two different answers, and
   * a call site reading the sentinel could not tell them apart. At
   * sg-nomad-01 the MEGAZO adapter's vendor login was failing, so
   * every `doors/<name>/state` key was absent and `L4_SLL_Door` -- a real door in
   * the building map -- did not appear on the console at all. False
   * here is what puts it on the screen as offline instead of leaving
   * it invisible, and what keeps it non-commandable.
   */
  hasLiveState: boolean;
  /**
   * Who last asked this door to do what, if the worker has seen a
   * request for it. Deliberately separate from `state`: a door that is
   * OPEN with no command newer than its opening was opened by nobody on
   * this platform -- the vendor controller, a badge, a hand -- and the
   * card must be able to say that rather than imply a press.
   */
  lastCommand?: DoorLastCommandView;
}

export function adaptDoor(d: unknown): DoorViewModel {
  const raw = d as Record<string, unknown>;
  const v1 = raw.v1_x !== undefined && raw.v1_y !== undefined
    ? { x: Number(raw.v1_x), y: Number(raw.v1_y) }
    : undefined;
  const v2 = raw.v2_x !== undefined && raw.v2_y !== undefined
    ? { x: Number(raw.v2_x), y: Number(raw.v2_y) }
    : undefined;
  return {
    id: String(raw.name ?? raw.id ?? ''),
    name: String(raw.name ?? raw.id ?? ''),
    level: String(raw.level ?? ''),
    state: (
      (raw.current_mode as { value?: number | string } | undefined)?.value ??
      raw.state ??
      raw.door_state ??
      'unknown'
    ) as DoorStateValue,
    doorType: typeof raw.door_type === 'number' ? raw.door_type : undefined,
    v1,
    v2,
    lastSeen: raw.lastSeen as string | undefined,
    siteId: raw.siteId as string | undefined,
    // This adapter is only ever handed a published state payload, so
    // reaching it at all is the evidence.
    hasLiveState: true,
  };
}

export type LiftMotionState = 'idle' | 'moving' | 'fault' | 'unknown';
export type LiftDoorState = 'open' | 'closed' | 'moving' | 'fault' | 'unknown';

export interface LiftViewModel {
  id: string;
  name: string;
  levels: string[];
  currentLevel?: string;
  destinationLevel?: string;
  motionState: LiftMotionState;
  /** Only meaningful when `motionState === 'moving'` — upstream's
   *  MOTION_UP/MOTION_DOWN, kept separate from `motionState` itself. */
  motionDirection?: 'up' | 'down';
  /**
   * Upstream `LiftState.current_mode`, mapped to the v1 vocabulary.
   * The SPA threw this away for a long time and rendered only the
   * collapsed `fault` boolean, which is how one red "Fault" chip came
   * to stand for five unrelated conditions — a disarmed bridge, an
   * unvalidated inventory, a dead controller, fire service, and a lift
   * in emergency all looked identical to an operator.
   *
   * Note MODE_EMERGENCY(5) collapses to 'unknown' here (there is no v1
   * equivalent), so `mode` alone cannot tell an emergency from an
   * unreported mode; `fault` below is what separates them.
   */
  mode?: LiftMode;
  /**
   * True when the lift's mode is one RMF only ever reports and never
   * accepts — MODE_OFFLINE(4) or MODE_EMERGENCY(5). Kept as its own
   * field rather than only folded into `motionState`, which loses
   * which of the two it was and overwrites the real motion.
   */
  fault?: boolean;
  doorState: LiftDoorState;
  /** Currently active RMF wire session, as reported by the lift
   *  itself — not necessarily ours. `undefined` when unclaimed. */
  rmfSessionId?: string;
  /**
   * The bridge's session phase, when a ride is running.
   *
   * Echoed back on "Release lift" so the bridge can refuse a
   * confirmation that no longer describes what is happening: a stalled
   * session can move the instant it un-stalls, and an operator's
   * confirmation of one step must never land on a different one.
   *
   * Optional because it only exists while a session does, and because
   * it arrives from a projection the api-server may not yet be
   * publishing — an older worker simply leaves it undefined, and the
   * Release button stays disabled rather than sending a phase it made
   * up.
   */
  sessionPhase?: string;
  /**
   * The adapter's whole session view (owner, wait, refusal, last_result,
   * in_flight...), carried through untouched. This is what decides which
   * verb buttons the drawer OFFERS: Release only on an advance-eligible
   * wait, Cancel only on an operator-owned session, Recover/End only on a
   * robot-owned one. `sessionPhase` above is the one field of it a 0.4.7
   * worker also published; undefined from such a worker, and the drawer
   * then offers Request Floor only.
   */
  session?: LiftSessionView;
  /** What the supervisor is still re-presenting on `lift_requests`. A
   *  `nexsoss-worker-*` id is a legacy request the worker drains. */
  standingRequest?: LiftStandingRequest;
  ref?: { x: number; y: number };
  /**
   * The cabin rectangle's rotation, from the registry's `v1_yaw`.
   *
   * WITHOUT IT THE RECTANGLE IS WRONG, and wrong in the direction that
   * matters: `heldLift` asks whether a robot is inside a car, and a
   * cabin drawn axis-aligned when it is not puts the long side the
   * wrong way round. At sg-nomad-01 the two sides differ by 0.15 m and
   * the margin is 0.225 m, so ignoring yaw can call a robot on the
   * landing "inside" -- and hide the Cancel button an operator needs.
   */
  refYaw?: number;
  width?: number;
  depth?: number;
  lastSeen?: string;
  siteId?: string;
  /**
   * Whether anything is publishing state for this car, as opposed to
   * the lift being known only from the building map's inventory
   * (`lifts_registry`). See `DoorViewModel.hasLiveState` for the
   * outage that made the distinction load-bearing; the lift list
   * follows the same rule so the two device lists cannot disagree
   * about what "the device is not there" means.
   *
   * False means every field below `levels` is an absence rather than a
   * reading, so no verb may be offered on it -- `liftDispatchStatus`
   * checks this before anything else for exactly that reason.
   */
  hasLiveState: boolean;
}

export type FireAlarmState = 'idle' | 'triggered' | 'unknown';

export interface FireAlarmViewModel {
  state: FireAlarmState;
  triggeredAt: number | null;
  lastSeen?: string;
  siteId?: string;
}

export function adaptFireAlarm(p: unknown): FireAlarmViewModel {
  const raw = p as Record<string, unknown> | null;
  if (!raw) return { state: 'unknown', triggeredAt: null };
  const trigger = Boolean(raw.trigger);
  const unix = typeof raw.unix_millis_time === 'number' ? raw.unix_millis_time : null;
  return {
    state: trigger ? 'triggered' : 'idle',
    triggeredAt: trigger ? unix : null,
    lastSeen: unix ? new Date(unix).toISOString() : undefined,
    siteId: raw.siteId as string | undefined,
  };
}

// The Beacon/Dispenser/Ingestor/Rio view models and their adapters
// were deleted along with the Devices Systems panel — no producer for
// those categories has ever existed on the v1 plane, so the adapters
// only ever ran against an empty cache.

export function adaptLift(l: unknown): LiftViewModel {
  const raw = l as Record<string, unknown>;
  const levels = Array.isArray(raw.levels)
    ? (raw.levels as unknown[]).map((lv) => {
        if (typeof lv === 'string') return lv;
        const r = lv as Record<string, unknown>;
        return String(r.name ?? r.id ?? r.level ?? '');
      }).filter((s) => s.length > 0)
    : [];
  return {
    id: String(raw.name ?? raw.id ?? ''),
    name: String(raw.name ?? raw.id ?? ''),
    levels,
    currentLevel: raw.current_floor as string | undefined,
    destinationLevel: raw.destination_floor as string | undefined,
    motionState: (raw.motion_state ?? raw.state ?? 'unknown') as LiftMotionState,
    doorState: (raw.door_state ?? raw.doorState ?? 'unknown') as LiftDoorState,
    ref: raw.ref_x !== undefined && raw.ref_y !== undefined
      ? { x: Number(raw.ref_x), y: Number(raw.ref_y) }
      : undefined,
    width: typeof raw.width === 'number' ? raw.width : undefined,
    depth: typeof raw.depth === 'number' ? raw.depth : undefined,
    lastSeen: raw.lastSeen as string | undefined,
    siteId: raw.siteId as string | undefined,
    // As in `adaptDoor`: this adapter only ever sees a published state
    // payload.
    hasLiveState: true,
  };
}
